import { Component, ChangeDetectionStrategy } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { ActivatedRoute } from '@angular/router';
import { Subscription, switchMap } from 'rxjs';
import { SubscriptionService } from 'src/app/features/subscription/subscription.service';
import { MinProjectDTO } from 'src/app/features/subscription/types/MinProjectDTO';
import { PublicStakeholderDTO } from 'src/app/features/subscription/types/PublicStakeholderDTO';
import { GlobalEditStakeholderComponent } from 'src/app/shared/components/global-edit-stakeholder/global-edit-stakeholder.component';
import { LoadingSpinnerOverlayComponent } from 'src/app/shared/components/loading-spinner-overlay/loading-spinner-overlay.component';
import { ToastrNotificationService } from 'src/app/shared/services/toastr-notification.service';
import { UrlParamsService } from 'src/app/shared/services/url-params.service';
import { RsvpDisplay } from 'src/app/shared/types/RsvpDisplay';
import { SubscriptionSourceType } from 'src/app/shared/types/SubscriptionSourceType';
import { ToastrType } from 'src/app/shared/types/ToastrType.enum';
import { RsvpPageService } from '../../rsvp-page.service';
import { LookupsDTO } from 'src/app/shared/types/LookupsDTO';

@Component({
  selector: 'app-edit-subscription-component',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    MatCheckboxModule,
    LoadingSpinnerOverlayComponent,
    GlobalEditStakeholderComponent,
  ],
  templateUrl: './rsvp-edit.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['../../rsvp.component.scss', './rsvp-edit.component.scss'],
})
export class RsvpEditComponent {
  rsvpDisplay: RsvpDisplay | undefined;

  stakeholderInfo: PublicStakeholderDTO = {} as PublicStakeholderDTO;
  isEdit: boolean = false;
  projectInfo: MinProjectDTO | null = null;
  private subscription: Subscription | null = null;
  states: any[] = [];
  stakeholderTypes: any[] = [];
  organizationTypes: any[] = [];
  commentTopics: any[] = [];
  commentChoices: any[] = [];
  travelList: LookupsDTO[] = [];

  TotalAttendees: number | null = null;
  MeetingGuid: string | null = null;

  constructor(
    private urlParamsService: UrlParamsService,
    private route: ActivatedRoute,
    private subscriptionService: SubscriptionService,
    private _rsvpService: RsvpPageService,
    private toastrNotification: ToastrNotificationService
  ) {
    this.rsvpDisplay = _rsvpService.setupFormDisplay();
  }

  ngOnInit(): void {
    const state = history.state;
    if (state && !state.isEdit) {
      this.stakeholderInfo = {
        ...this.stakeholderInfo,
        LastName: state.LastName,
        Mail1ZipCode: state.Mail1ZipCode,
        StakeEmail: state.StakeEmail,
        SubscribeSourceId: SubscriptionSourceType.RSVP,
      };
      this.isEdit = state.isEdit;

      this.TotalAttendees = state.TotalAttendees;
      this.MeetingGuid = state.MeetingGuid;
    }

    if (this.urlParamsService.stakeGuid) {
      this.loadStakeholderDetails();
    }

    if (this.rsvpDisplay?.publicMeeting$) {
      this.rsvpDisplay.publicMeeting$.subscribe({
        next: (meeting) => {
          this.projectInfo = {
            ProjectGuid: meeting.ProjectGuid,
            ProjectName: meeting.ProjectName,
            ProjectDescription: meeting.ProjectDescription,
            ProjectNumber: meeting.ProjectNumber,
            ProjectId: meeting.ProjectId,
          } as MinProjectDTO;
        },
        error: (err) => {
          console.error('Error loading public meeting data:', err);
        },
      });
    }
  }

  loadStakeholderDetails(): void {
    this.route.queryParams
      .pipe(
        switchMap((params) => {
          const stakeGuid = params['stakeGuid'];
          const projectGuid = params['projectGuid'];
          if (stakeGuid && projectGuid) {
            return this.subscriptionService.getStakeholderByProjectAndStakeGuid(
              stakeGuid,
              projectGuid
            );
          }
          // Return an empty observable if parameters are missing
          return [];
        })
      )
      .subscribe({
        next: (response) => {
          if (response) {
            this.isEdit = true;
            this.stakeholderInfo = response;
            this.stakeholderInfo.SubscribeSourceId = 4;
            if (response.TravelList) {
              this.travelList = response.TravelList;
            }
          }
        },
        error: (error) => {
          console.error('Error fetching stakeholder details:', error);
        },
      });
  }

  goBack(): void {
    this.urlParamsService.navigateWithParams('/rsvp/search');
  }

  handleStakeholderEditCreateResult(event: { status: string; data: any }) {
    if (!this.TotalAttendees || !this.MeetingGuid) {
      return;
    }

    switch (event.status) {
      case 'success':
        this._rsvpService
          .post(this.MeetingGuid, event.data.StakeGuid, this.TotalAttendees)
          .subscribe({
            complete: () => {
              if (this.urlParamsService.projectGuid && event.data.StakeGuid) {
                this.triggerEmail(
                  this.urlParamsService.projectGuid,
                  event.data.StakeGuid,
                  event.data.IsReceiveEmailUpdate ?? false
                );
              }
              this.urlParamsService.navigateWithParams('/rsvp/confirm');
            },
            error: (err) => {
              console.error(err);
            },
          });
        break;
      case 'error':
        console.error('Error during service call:', event.data);

        break;
    }
  }

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  triggerEmail(projectGuid: string, stakeGuid: string, isSubscribed: boolean = false) {
    this.subscriptionService
      .triggerConfirmationSubscriptionEmail(projectGuid, stakeGuid, isSubscribed)
      .subscribe({
        error: (error) => {
          console.error('Error sending confirmation email:', error);
        },
      });
  }
}
